import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLazyGetProductBookingConfQuery } from '@timum/timum_pdk';

import {
  selectSelectedProduct,
  selectIsLocalisationAllowed,
  selectAreCustomFieldsAllowed,
  setProductAppConfigOverride,
} from '../../slices/appState';
import { processProductOverride } from '../../utils/configUtils';

/**
 * ProductBookingConfigHandler
 *
 * Lazy-loads product-scoped bookingConfig overrides from the public PDK
 * endpoint the first time a customer selects a product. Dispatches the
 * processed override into per-instance state, which `selectAppConfig` then
 * layers on top of the base (channel) appConfig.
 *
 * Behavior:
 *  - No product selected   → clears any existing override.
 *  - New product selected  → triggers the lazy query; on success dispatches
 *                            the processed override (or null for empty/404).
 *  - Fetch error           → silent fallback: log + clear override. The
 *                            booking funnel is never blocked on this fetch.
 *  - Re-select same product → RTK Query cache serves the result, no network
 *                            round-trip (keepUnusedDataFor is 2h).
 *
 * Paywall gating mirrors ConfigHandler.mergeConfigs — an override cannot
 * smuggle in localisation or custom fields the account hasn't paid for.
 */
const ProductBookingConfigHandler = () => {
  const dispatch = useDispatch();
  const selectedProduct = useSelector(selectSelectedProduct);
  const isLocalisationAllowed = useSelector(selectIsLocalisationAllowed);
  const areCustomFieldsAllowed = useSelector(selectAreCustomFieldsAllowed);

  const [fetchProductConfig] = useLazyGetProductBookingConfQuery();

  // The PDK endpoint identifies the product via its `ref` (fully qualified
  // product reference). We fall back to the uuid when a ref isn't present
  // (e.g. in tests that mount plain product objects).
  const prdRef = selectedProduct?.ref ?? selectedProduct?.uuid;

  // Tracks the prdRef of the most recent effect run so async resolutions can
  // detect a product swap that happened while their fetch was in flight
  // (including synchronous cache-path resolutions, where the cancellation
  // cleanup runs only *after* the async body begins resolving).
  const latestPrdRefRef = useRef(null);

  useEffect(() => {
    latestPrdRefRef.current = prdRef ?? null;
    let cancelled = false;

    if (!prdRef) {
      dispatch(setProductAppConfigOverride(null));
      return undefined;
    }

    (async () => {
      try {
        const result = await fetchProductConfig(
          { prdRef },
          /* preferCacheValue */ true,
        ).unwrap();

        // Bail if this effect run was superseded by a newer product selection
        // before our promise resolved (pure cache-hit paths still resolve
        // after the next effect has set latestPrdRefRef).
        if (cancelled || latestPrdRefRef.current !== prdRef) return;

        const processed = processProductOverride(result, {
          isLocalisationAllowed,
          areCustomFieldsAllowed,
        });
        dispatch(setProductAppConfigOverride(processed));
      } catch (error) {
        if (cancelled || latestPrdRefRef.current !== prdRef) return;
        // Silent fallback — booking funnel must never block on override load.
        // A 404 is expected when a product has no override saved, so downgrade
        // that to a debug log.
        const isMissing = error?.status === 404;
        const log = isMissing ? console.debug : console.warn;
        log(
          '[bookingjs] product bookingConfig fetch returned no override — ' +
            'falling back to base config',
          error,
        );
        dispatch(setProductAppConfigOverride(null));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    prdRef,
    isLocalisationAllowed,
    areCustomFieldsAllowed,
    fetchProductConfig,
    dispatch,
  ]);

  return null;
};

ProductBookingConfigHandler.displayName = 'ProductBookingConfigHandler';

export default ProductBookingConfigHandler;
